/**
 * The circuit's venues: where a fight is held, and who holds the room.
 *
 * There are four arenas and thirteen fighters, so a room is **shared** — and
 * the way it is shared is the whole of what taking one means. Each of the four
 * arenas is one venue (`venues.json`), and every ranked fighter **lives** in one
 * of them (`home` in `ladder.json`): three or four fighters to a room. But a
 * venue has exactly **one holder** at a time. The residents are the people who
 * fight out of it; the holder is the one whose room it is. So taking Hoyt Street
 * is not taking one fighter's home off them — it is taking the room off the
 * person holding it, in front of everybody else who lives there.
 *
 * The rules, all of them:
 *
 *   - **A room that is held is where fights happen.** If either fighter holds
 *     one, the fight is in whichever of their rooms has gone longest without a
 *     fight — ties to the higher-ranked fighter's. So every held room keeps
 *     coming back onto the line, whoever holds it and from whichever side of
 *     the ladder they are challenged: a room held by somebody *below* you is
 *     still one you can go and take. (The first version had the higher-ranked
 *     fighter always host, and a champion never left their own rooms again —
 *     a room held from underneath them could not be reached at all.)
 *   - **Otherwise the higher-ranked fighter hosts**, at home. A host with no
 *     home (the player, who lives nowhere until they take somewhere) goes to
 *     the other fighter's. A challenger travels.
 *   - **A venue changes hands when its holder loses in it.** Winning at
 *     somebody's home takes it only if they were holding it; a room held by
 *     somebody who is not in the fight is not on the line. A draw moves nothing.
 *   - **Holding a room is having it behind you.** The holder starts every fight
 *     in their own room with the crowd on their side (the `home` heat event),
 *     and has to defend it every time it comes up.
 *   - **And it pays.** Every fight has a purse — the player's card, the churn's
 *     and Junior's alike — and the room it is fought in takes `takings.cut` of
 *     it for whoever holds the room, on top: the house pays the room, the
 *     fighters are paid in full. It goes on the holder's **tab**, room by room,
 *     and the tab is collected when the holder next fights — in any room,
 *     against anybody. A tab remembers at most `takings.tab` fights a room, so
 *     a holder who stops fighting stops being paid: the rooms keep earning,
 *     and the oldest night falls off the bottom.
 *
 * The same rules for everybody: the player's fights, the churn across the city
 * and Junior's climb all go through `where` and `fought`, the way every result
 * goes through the ladder's one `settle`.
 *
 * Pure. The ladder owns an instance, and its snapshot carries who holds what.
 */

import VENUES from './venues.json';

export class VenueConfigError extends Error {}

export { VENUES };

/**
 * Throws on a set of venues the campaign could not be fought in.
 *
 * The manifest check, in the shape `validateMeshes` has: a fighter with no home,
 * a home that is not a venue, a venue nobody holds, a holder who does not live
 * there or is not on the ladder when the career starts, and — when it is handed
 * the list of arenas — a venue fought in an arena that does not exist, or an
 * arena no venue uses.
 *
 * The player is the one roster entry that must **not** have a home. They have
 * none until they take one, and a file that gave them one would hand them a
 * room on the first night.
 */
export function validateVenues(config = VENUES, { roster = null, arenas = null } = {}) {
  const list = config?.venues;
  if (!Array.isArray(list) || list.length === 0) {
    throw new VenueConfigError('there are no venues; a card has to be fought somewhere');
  }

  // What a room pays. Optional — a set of venues with no `takings` pays
  // nothing, which is what a unit fixture wants — but if it is there it has to
  // mean something: a cut of zero is a room that pays nothing while claiming
  // to, and a cut of half the purse or more is a room worth more than fighting.
  const takings = config.takings;
  if (takings !== undefined) {
    if (!(takings?.cut > 0 && takings.cut < 0.5)) {
      throw new VenueConfigError(`takings.cut is ${JSON.stringify(takings?.cut)}; want a share of a purse, over 0 and under 0.5`);
    }
    if (!Number.isInteger(takings.tab) || takings.tab < 1) {
      throw new VenueConfigError(`takings.tab is ${JSON.stringify(takings.tab)}; want at least one fight`);
    }
  }

  const ids = new Set();
  for (const venue of list) {
    if (!venue?.id || typeof venue.id !== 'string') {
      throw new VenueConfigError(`a venue has an id of ${JSON.stringify(venue?.id)}`);
    }
    if (ids.has(venue.id)) throw new VenueConfigError(`"${venue.id}" is a venue twice`);
    ids.add(venue.id);
    for (const field of ['name', 'arena', 'where']) {
      if (typeof venue[field] !== 'string' || !venue[field].trim()) {
        throw new VenueConfigError(`"${venue.id}" has a ${field} of ${JSON.stringify(venue[field])}`);
      }
    }
    if (arenas && !arenas.includes(venue.arena)) {
      throw new VenueConfigError(
        `"${venue.id}" is fought in "${venue.arena}", which is not an arena; there is ${arenas.join(', ')}`
      );
    }
  }

  // Every place there is to fight in is somebody's. An arena built and never
  // booked is a room the campaign cannot reach.
  if (arenas) {
    for (const arena of arenas) {
      if (!list.some((venue) => venue.arena === arena)) {
        throw new VenueConfigError(`no venue is fought in "${arena}"; nothing on the circuit would use it`);
      }
    }
  }

  if (!roster) return config;

  const byId = new Map(roster.map((entry) => [entry.id, entry]));
  for (const entry of roster) {
    if (entry.player) {
      if (entry.home !== undefined) {
        throw new VenueConfigError(
          `the player lives at ${JSON.stringify(entry.home)}; nobody has a home until they take one`
        );
      }
      continue;
    }
    if (typeof entry.home !== 'string' || !entry.home) {
      throw new VenueConfigError(`"${entry.id}" has no home`);
    }
    if (!ids.has(entry.home)) {
      throw new VenueConfigError(
        `"${entry.id}" lives at "${entry.home}", which is not a venue; there is ${[...ids].join(', ')}`
      );
    }
  }

  for (const venue of list) {
    if (!venue.holder) throw new VenueConfigError(`"${venue.id}" is held by nobody`);
    const holder = byId.get(venue.holder);
    if (!holder) {
      throw new VenueConfigError(`"${venue.id}" is held by "${venue.holder}", who is not on the roster`);
    }
    if (holder.player) {
      throw new VenueConfigError(`"${venue.id}" starts held by the player, who has to take it`);
    }
    // Junior is on the roster and not on the ladder. A room held by somebody
    // who has no rank is a room nobody is holding.
    if (holder.debut) {
      throw new VenueConfigError(
        `"${venue.id}" is held by "${holder.id}", who is not on the ladder when the career starts`
      );
    }
    if (holder.home !== venue.id) {
      throw new VenueConfigError(
        `"${venue.id}" is held by "${holder.id}", who lives at "${holder.home}"; a room starts with one of its own`
      );
    }
  }

  return config;
}

/**
 * Who holds what, and where the next fight is.
 *
 * `hosted` and `taken` are a shared clock rather than seconds: the order things
 * happened in is all either of them is for. `hosted` picks which held room a
 * fight goes to (the one that has waited longest), and `taken` picks which one
 * the player calls home (the one they took last).
 */
export function createVenues({ config = VENUES, roster = [], playerId = null } = {}) {
  const list = config.venues;
  const byId = new Map(list.map((venue) => [venue.id, venue]));
  const homes = new Map(roster.filter((entry) => !entry.player && entry.home).map((entry) => [entry.id, entry.home]));

  /** venue id -> the fighter holding it. */
  const holders = new Map();
  /** venue id -> the tick it last hosted a fight. */
  const hosted = new Map();
  /** venue id -> the tick its current holder took it. */
  const taken = new Map();
  let tick = 0;
  /** What a room pays: `{ cut, tab }`, or null for rooms that pay nothing. */
  const takings = config.takings ?? null;
  /** fighter id -> venue id -> the cuts not yet collected, oldest first. */
  const tabs = new Map();
  /** fighter id -> `{ purses, rooms }`, everything they have been paid. */
  const earnings = new Map();

  function seed() {
    holders.clear();
    hosted.clear();
    taken.clear();
    tabs.clear();
    earnings.clear();
    tick = 0;
    for (const venue of list) {
      holders.set(venue.id, venue.holder);
      hosted.set(venue.id, 0);
      taken.set(venue.id, 0);
    }
  }
  seed();

  /** What a fighter holds, in the file's order. */
  const held = (id) => list.filter((venue) => holders.get(venue.id) === id).map((venue) => venue.id);

  /**
   * Where a fighter is from, as a venue. The roster's answer for everybody but
   * the player, who lives wherever they took last and nowhere before that.
   */
  function homeOf(id) {
    if (id !== playerId) return homes.get(id) ?? null;
    const mine = held(id);
    if (!mine.length) return null;
    return mine.reduce((last, venue) => (taken.get(venue) > taken.get(last) ? venue : last));
  }

  /**
   * Where `a` and `b` fight. A room either of them holds, the one that has
   * waited longest; else the higher-ranked fighter's home, else the other's.
   * `rankOf` is the ladder's, and a fighter with no rank (nobody, in practice)
   * is treated as the lower.
   */
  function where(a, b, rankOf) {
    const ra = rankOf(a) ?? Infinity;
    const rb = rankOf(b) ?? Infinity;
    const [host, guest] = ra <= rb ? [a, b] : [b, a];
    const rooms = [...held(host), ...held(guest)];
    if (rooms.length) {
      // Stable: the host's rooms come first, so on a tie they keep the fight.
      return rooms.reduce((oldest, venue) => (hosted.get(venue) < hosted.get(oldest) ? venue : oldest));
    }
    return homes.get(host) ?? homes.get(guest) ?? null;
  }

  /**
   * A fight was held at `id`. Returns the change of hands, or null.
   *
   * `winner` null is a draw: the room hosted a fight and nothing else happened.
   */
  function fought(id, { winner = null, loser = null, purse = 0 } = {}) {
    if (!byId.has(id)) return null;
    tick += 1;
    hosted.set(id, tick);
    const holder = holders.get(id);
    // The room's cut, to whoever held it when the fight was put on — before
    // the result, so a holder who loses the room still has that night's money.
    if (takings && holder && purse > 0) {
      const cut = Math.round(purse * takings.cut);
      if (cut > 0) {
        const mine = tabs.get(holder) ?? new Map();
        const owed = [...(mine.get(id) ?? []), cut];
        // The tab remembers `tab` fights a room. The oldest falls off.
        mine.set(id, owed.slice(-takings.tab));
        tabs.set(holder, mine);
      }
    }
    if (!winner || winner === loser || holder !== loser) return null;
    holders.set(id, winner);
    taken.set(id, tick);
    return Object.freeze({ id, from: loser, to: winner });
  }

  const ledger = (id) => {
    if (!earnings.has(id)) earnings.set(id, { purses: 0, rooms: 0 });
    return earnings.get(id);
  };

  /**
   * What `id` is owed, room by room, without collecting it. Empty for
   * somebody nothing is owed.
   */
  function owed(id) {
    const mine = tabs.get(id);
    if (!mine) return [];
    return list
      .filter((venue) => mine.get(venue.id)?.length)
      .map((venue) => {
        const cuts = mine.get(venue.id);
        return Object.freeze({
          id: venue.id,
          name: venue.name,
          amount: cuts.reduce((sum, cut) => sum + cut, 0),
          fights: cuts.length,
        });
      });
  }

  return {
    config,
    list,
    takings,
    get: (id) => byId.get(id) ?? null,
    owed,

    /**
     * `id` turned up to fight: their tab, emptied, as `{ total, rooms }`. The
     * rooms are each `{ id, name, amount, fights }`. Recorded on their ledger.
     */
    collect(id) {
      const rooms = owed(id);
      tabs.delete(id);
      const total = rooms.reduce((sum, room) => sum + room.amount, 0);
      ledger(id).rooms += total;
      return Object.freeze({ total, rooms: Object.freeze(rooms) });
    },

    /** A purse share paid to `id`, onto their ledger. */
    paid(id, amount) {
      if (amount > 0) ledger(id).purses += amount;
    },

    /** Everything `id` has been paid: `{ purses, rooms }`. */
    earnings: (id) => Object.freeze({ ...(earnings.get(id) ?? { purses: 0, rooms: 0 }) }),
    holderOf: (id) => holders.get(id) ?? null,
    held,
    homeOf,
    where,
    fought,

    snapshot() {
      return Object.fromEntries(
        list.map((venue) => [
          venue.id,
          {
            holder: holders.get(venue.id),
            hosted: hosted.get(venue.id),
            taken: taken.get(venue.id),
            // Who is owed what out of this room. A tab is money a fighter has
            // earned and not yet picked up; a reload must not lose it.
            tabs: Object.fromEntries(
              [...tabs].filter(([, mine]) => mine.get(venue.id)?.length).map(([who, mine]) => [who, [...mine.get(venue.id)]])
            ),
          },
        ])
      );
    },

    /** Everybody's ledger, for the save. */
    snapshotEarnings() {
      return Object.fromEntries([...earnings].map(([id, row]) => [id, { ...row }]));
    },
    restoreEarnings(saved = {}) {
      earnings.clear();
      for (const [id, row] of Object.entries(saved ?? {})) {
        earnings.set(id, { purses: Number(row?.purses) || 0, rooms: Number(row?.rooms) || 0 });
      }
    },

    /**
     * Who holds what, put back — or refused whole. Every venue the file declares
     * must be in the save, held by somebody `known` (on the ladder), and nothing
     * else may be. A save naming a room that no longer exists is from a
     * different circuit.
     */
    restore(saved, known) {
      if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return false;
      const keys = Object.keys(saved);
      if (keys.length !== list.length || keys.some((id) => !byId.has(id))) return false;
      for (const venue of list) {
        const row = saved[venue.id];
        if (!row || typeof row.holder !== 'string' || !known(row.holder)) return false;
      }
      tick = 0;
      tabs.clear();
      for (const venue of list) {
        const row = saved[venue.id];
        for (const [who, cuts] of Object.entries(row.tabs ?? {})) {
          if (!known(who) || !Array.isArray(cuts)) continue;
          const mine = tabs.get(who) ?? new Map();
          mine.set(venue.id, cuts.map((cut) => Number(cut) || 0).slice(-(takings?.tab ?? cuts.length)));
          tabs.set(who, mine);
        }
        holders.set(venue.id, row.holder);
        hosted.set(venue.id, Number(row.hosted) || 0);
        taken.set(venue.id, Number(row.taken) || 0);
        tick = Math.max(tick, hosted.get(venue.id), taken.get(venue.id));
      }
      return true;
    },

    reset: seed,
  };
}
